import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  Award,
  Sparkles,
  CheckCircle,
  XCircle,
  HelpCircle,
  CalendarClock,
  History,
  ChevronDown,
  ChevronUp,
  Loader2,
  Quote,
  Undo2,
} from 'lucide-react';
import type { Employee, ReviewData } from '../index';
/* CO-447/CO-449 — SEAM S2. The strength view models are OWNED and EXPORTED by
   `models/employee.ts` and the fetchers by `api/Employee/skills.ts`; this panel declares NOTHING of
   its own. Spec §3.0.1 tells this lane to declare its own copies in the snapshot api module — that
   is exactly the duplication §3.3.3 exists to delete (three verbatim `Strength` interfaces had
   already grown that way), so it is deliberately not done. Reported as a deviation. */
import type {
  SkillEvidenceSourceType,
  StrengthConfidence,
  UserStrengthEvidenceView,
  UserStrengthView,
} from '../../../models/employee';
import {
  getUserStrengthEvidence,
  getUserStrengths,
  suppressStrength,
  unsuppressStrength,
} from '../../../api/Employee/skills';
import { getApiErrorMessage } from '../../../utils/apiErrors';
import { appDate } from '../../../utils/appDateFormat';

interface SurfedStrengthsSkillsPanelProps {
  employee: Employee;
  reviewData: ReviewData;
  /** A snapshot past Draft cannot be written to — see the same prop on `DecisionOutputsPanel`. */
  readOnly?: boolean;
  onStrengthsValidationUpdate?: (validation: Record<string, unknown>) => void;
}

interface ReviewItem {
  id: string;
  name: string;
  type: 'skill';
  proficiency: string;
  endorsements: number;
  status: string | null;
}

interface StatusOption {
  value: string;
  label: string;
  Icon: typeof CheckCircle;
  color: string;
  bgColor: string;
  borderColor: string;
}

/**
 * The four members of the server's `SkillEvidenceType` enum
 * (`Data/Employee/Entities/SkillEvidence.cs`). Enums are STRINGS on the wire — `Program.cs` registers
 * a global `StringEnumConverter` — so this is never a number.
 */
type KnownEvidenceType =
  | 'SkillAdded'
  | 'Endorsement'
  | 'ProficiencyChange'
  | 'Feedback';

/**
 * A value this bundle has not been taught still has to render. A browser tab holding the previous SPA
 * bundle keeps talking to the new backend for at least one rollback window (CLAUDE.md §ZDD rule 5), so
 * a fifth enum member must fall through to its raw name rather than blanking the row.
 */
type EvidenceTypeName = KnownEvidenceType | (string & NonNullable<unknown>);

interface InPeriodEvidence {
  skillName: string;
  evidenceType: EvidenceTypeName;
  /** ISO-8601 UTC, or absent on a row that carries no date. */
  evidenceDate: string | null;
}

/**
 * CO-440 — the `inPeriod` block of `strengthsSummary`: the skill evidence that actually landed inside
 * the snapshot's month. ADDITIVE — it is absent on every row written before CO-440, and the panel must
 * render exactly as it did before when it is.
 */
interface InPeriodSummary {
  evidenceCount: number;
  distinctSkills: number;
  skills: InPeriodEvidence[];
}

/** The slice this panel reads, same three fields the page itself reads (`index.tsx:748-750`). */
interface UserSliceShape {
  userId: string;
  role: string;
  isManager: boolean;
}

/**
 * CO-449 — who may suppress, mirrored from the server so the UI never offers a control that is
 * guaranteed to 403. `SkillService.LoadStrengthForSuppressionAsync` (`SkillService.cs:1113-1154`)
 * refuses the SUBJECT first and on its own — an HR person may not suppress their OWN strength —
 * then requires Hr/Admin or a direct-manager relationship, and refuses Operator outright.
 * Self-suppression would let an employee delete their own strengths and move their department's Q3.
 */
const SUPPRESSION_ROLES = ['Hr', 'Admin'];

/** Exhaustive by construction — TS rejects a missing member, which is the point of the Record. */
const CONFIDENCE_LABEL_KEY: Record<StrengthConfidence, string> = {
  Low: 'strengths.confidence.low',
  Medium: 'strengths.confidence.medium',
  High: 'strengths.confidence.high',
};

const CONFIDENCE_CHIP_CLASS: Record<StrengthConfidence, string> = {
  Low: 'text-muted-foreground bg-muted',
  Medium: 'text-amber-700 bg-amber-50',
  High: 'text-emerald-700 bg-emerald-50',
};

const EVIDENCE_SOURCE_LABEL_KEY: Record<SkillEvidenceSourceType, string> = {
  Unknown: 'strengths.evidence.source.unknown',
  Recognition: 'strengths.evidence.source.recognition',
  Goal: 'strengths.evidence.source.goal',
  GoalUpdate: 'strengths.evidence.source.goalUpdate',
  CheckIn: 'strengths.evidence.source.checkIn',
  TeamHubPost: 'strengths.evidence.source.teamHubPost',
  TeamHubPostComment: 'strengths.evidence.source.teamHubPostComment',
  ManagerReflectionResponse: 'strengths.evidence.source.managerReflectionResponse',
  ManualEndorsement: 'strengths.evidence.source.manualEndorsement',
};

/** §3.3.2 — "an expander showing up to 3 quoted spans". The cap is the contract, not a page size. */
const MAX_QUOTED_SPANS = 3;

/**
 * A validation entry inside the per-snapshot `strengthsValidation` blob.
 *
 * 🔴 `type` is what keeps the two lists apart. Until CO-447 every entry was a `'skill'`, and a
 * derived strength very often carries the SAME label as a declared skill (that is what
 * `UserStrength.SkillId` links). Matching on `name` alone would let a strength verdict re-hydrate
 * onto a skill card and vice versa — one manager click silently showing up in two places. Entries
 * written before CO-447 are all `'skill'`; a missing `type` is therefore read as `'skill'`.
 */
interface ValidationEntry {
  name: string;
  type?: string;
  status: string;
}

const SurfedStrengthsSkillsPanel = ({
  employee,
  reviewData,
  readOnly = false,
  onStrengthsValidationUpdate,
}: SurfedStrengthsSkillsPanelProps) => {
  const { t, i18n } = useTranslation();

  // Parse real strengths from snapshot data.
  //
  // `strengthsSummary` is a PRE-SERIALIZED JSON string, so it never passes through the response pipeline that
  // camelCases every other field on the wire. Three shapes exist in the wild and all three must render:
  //   1. current  — {"topSkills":[{"skillName","proficiency","endorsementCount"}], "totalSkills":n}
  //   2. legacy A — the same, but with PascalCase members, from the EF projection being serialized with the
  //                 default naming policy. Reading only camelCase gave `undefined` for all three members, so the
  //                 tab rendered one BLANK card per skill.
  //   3. legacy B — {"top":["Communication",...], "endorsements":n} written by the dev seeder. It carries no
  //                 topSkills key at all, so the tab showed its "no data" empty state on every seeded tenant.
  // Rows in shapes 2 and 3 are never rewritten, so this tolerance is permanent, not a migration window.
  const pick = <T,>(o: Record<string, unknown>, ...names: string[]): T | undefined => {
    for (const n of names) {
      if (o?.[n] !== undefined && o[n] !== null) return o[n] as T;
    }
    return undefined;
  };

  // CO-434 — the SKILLS SUMMARY, read straight off the snapshot for DISPLAY only. It is a separate
  // column from `strengthsValidation`, and only the validation ever round-trips back to the server.
  const initialItems = (): ReviewItem[] => {
    try {
      if (!reviewData.snapshot?.strengthsSummary) return [];
      const data = JSON.parse(reviewData.snapshot.strengthsSummary) ?? {};
      const raw: unknown[] =
        pick<unknown[]>(data, 'topSkills', 'TopSkills') ??
        pick<unknown[]>(data, 'top', 'Top') ??
        [];
      if (!Array.isArray(raw)) return [];

      return raw
        .map((entry, idx) => {
          // Shape 3 stores bare strings rather than objects.
          const obj =
            typeof entry === 'string'
              ? { skillName: entry }
              : ((entry ?? {}) as Record<string, unknown>);
          const name = pick<string>(obj, 'skillName', 'SkillName');
          if (!name) return null;
          return {
            id: `skill_${idx}`,
            name,
            type: 'skill' as const,
            proficiency: pick<string>(obj, 'proficiency', 'Proficiency') ?? '',
            endorsements:
              pick<number>(obj, 'endorsementCount', 'EndorsementCount') ?? 0,
            status: null as string | null,
          };
        })
        .filter((i): i is ReviewItem => i !== null);
    } catch {
      return [];
    }
  };

  /**
   * CO-440 — the IN-PERIOD evidence, read off the same `strengthsSummary` column.
   *
   * 🔴 Additive and OPTIONAL. `inPeriod` is absent from every snapshot generated before CO-440, and
   * such a row must render exactly as it does today — so this returns `null`, not an empty summary,
   * when the key is missing. The distinction is the whole point of the step:
   *   `null`                      → this snapshot never measured the month at all (legacy row).
   *   present, `evidenceCount: 0` → measured, and nothing happened that month.
   * Those are different statements about a person and are rendered as different things.
   *
   * PascalCase is tolerated alongside camelCase for the same reason the list above tolerates it — the
   * column holds pre-serialized JSON that bypasses the response pipeline's naming policy.
   */
  const readInPeriod = (): InPeriodSummary | null => {
    try {
      if (!reviewData.snapshot?.strengthsSummary) return null;
      const data = JSON.parse(reviewData.snapshot.strengthsSummary) ?? {};
      const block = pick<Record<string, unknown>>(data, 'inPeriod', 'InPeriod');
      if (!block || typeof block !== 'object') return null;

      const rawSkills = pick<unknown[]>(block, 'skills', 'Skills') ?? [];
      const skills = (Array.isArray(rawSkills) ? rawSkills : [])
        .map((entry) => {
          const obj = (entry ?? {}) as Record<string, unknown>;
          const name = pick<string>(obj, 'skillName', 'SkillName');
          if (!name) return null;
          return {
            skillName: name,
            // A string on the wire, always. An unrecognised value renders verbatim — see EvidenceTypeName.
            evidenceType:
              pick<string>(obj, 'evidenceType', 'EvidenceType') ?? '',
            evidenceDate:
              pick<string>(obj, 'evidenceDate', 'EvidenceDate') ?? null,
          };
        })
        .filter((s): s is InPeriodEvidence => s !== null);

      return {
        evidenceCount:
          pick<number>(block, 'evidenceCount', 'EvidenceCount') ?? skills.length,
        distinctSkills:
          pick<number>(block, 'distinctSkills', 'DistinctSkills') ??
          new Set(skills.map((s) => s.skillName)).size,
        skills,
      };
    } catch {
      return null;
    }
  };

  const inPeriod = readInPeriod();

  /**
   * Apply the validations already recorded for these skills.
   *
   * CO-432/CO-434 — the source is `reviewData.strengths`, the LIVE page state. The page seeds that
   * from `snapshot.strengthsValidation` (the HR verdicts) and never from `strengthsSummary` (the
   * skills list), which is the swap that used to let an untouched tab overwrite the validation
   * column with `{topSkills:[…]}`. Reading page state also means an unsaved verdict survives a tab
   * switch instead of snapping back to the server copy.
   */
  const savedValidations = (): ValidationEntry[] => {
    const saved = reviewData.strengths as unknown as { validations?: ValidationEntry[] } | null;
    const validations = saved?.validations;
    return Array.isArray(validations) ? validations : [];
  };

  const loadSavedValidations = (items: ReviewItem[]): ReviewItem[] => {
    const validations = savedValidations();
    if (validations.length === 0) return items;
    return items.map((item) => {
      // `(v.type ?? 'skill')` — pre-CO-447 rows are all skills, so an absent type is a skill and
      // this reads exactly as it did before. What it now refuses is a `'strength'` entry bleeding
      // onto a same-named skill card.
      const match = validations.find(
        (v) => v.name === item.name && (v.type ?? 'skill') === 'skill',
      );
      return match ? { ...item, status: match.status } : item;
    });
  };

  const [items, setItems] = useState<ReviewItem[]>(() =>
    loadSavedValidations(initialItems()),
  );

  /* ────────────────────────────────────────────────────────────────────────────────────────────
     CO-447 — the OBSERVED strengths.

     🔴 These do NOT come from the snapshot. `strengthsSummary` is a per-month photograph of the
     DECLARED skill profile; a derived strength is a standing fact about the person that the
     nightly detector maintains in `UserStrengthBase`. Reading it live is what makes "Not really"
     able to change anything at all — see the suppression note on `handleStrengthStatusChange`.
     ──────────────────────────────────────────────────────────────────────────────────────────── */
  const myUserId = useSelector((state: { user: UserSliceShape }) => state.user.userId);
  const role = useSelector((state: { user: UserSliceShape }) => state.user.role);
  const isManager = useSelector((state: { user: UserSliceShape }) => state.user.isManager);

  const viewerIsSubject = !!myUserId && myUserId === employee.id;
  const canSuppress =
    !viewerIsSubject && (SUPPRESSION_ROLES.includes(role) || isManager);

  const [strengths, setStrengths] = useState<UserStrengthView[]>([]);
  const [strengthsLoading, setStrengthsLoading] = useState(true);
  /**
   * A 403 is NOT an error to shout about here. `SkillService.GetUserStrengths` (`:936-963`) allows
   * self / direct-subordinate / Hr — an **Admin** who is neither Hr nor this person's manager can
   * author the snapshot and still be refused the strengths. That is a legitimate outcome, so the
   * section is simply absent, exactly as it is on a build that has no strengths at all. Anything
   * else (a 500, a dropped connection) is a real failure and says so.
   */
  const [strengthsDenied, setStrengthsDenied] = useState(false);
  const [strengthsError, setStrengthsError] = useState<string | null>(null);
  const [strengthStatuses, setStrengthStatuses] = useState<Record<number, string>>({});
  const [expandedStrengthId, setExpandedStrengthId] = useState<number | null>(null);
  const [evidenceByStrength, setEvidenceByStrength] = useState<
    Record<number, UserStrengthEvidenceView[]>
  >({});
  const [evidenceLoadingId, setEvidenceLoadingId] = useState<number | null>(null);
  const [evidenceErrorIds, setEvidenceErrorIds] = useState<number[]>([]);
  const [suppressBusyId, setSuppressBusyId] = useState<number | null>(null);
  const [suppressError, setSuppressError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const employeeId = employee.id;
    if (!employeeId) {
      // No subject, nothing to load. Leaving `loading` true would spin forever.
      setStrengthsLoading(false);
      setStrengths([]);
      return;
    }

    setStrengthsLoading(true);
    setStrengthsDenied(false);
    setStrengthsError(null);
    // Clear on a SUBJECT change: showing the previous person's strengths under the new person's
    // name for the length of one request is the worst possible failure on this particular tab.
    setStrengths([]);
    setStrengthStatuses({});
    setExpandedStrengthId(null);
    setEvidenceByStrength({});
    setSuppressError(null);

    getUserStrengths(employeeId)
      .then((result) => {
        if (cancelled) return;
        const payload = result?.payload ?? [];
        setStrengths(payload);
        // Re-hydrate the manager's prior verdicts for THIS month, keyed by label + type.
        const validations = savedValidations();
        const seeded: Record<number, string> = {};
        payload.forEach((s) => {
          const match = validations.find(
            (v) => v.name === s.label && v.type === 'strength',
          );
          if (match?.status && match.status !== 'pending') seeded[s.id] = match.status;
        });
        setStrengthStatuses(seeded);
      })
      .catch((err) => {
        if (cancelled) return;
        const e = (err ?? {}) as {
          __httpStatus?: number;
          status?: number;
          response?: { status?: number };
        };
        const status = e.__httpStatus ?? e.response?.status ?? e.status;
        if (status === 403) {
          setStrengthsDenied(true);
          return;
        }
        setStrengthsError(getApiErrorMessage(err, t, t('strengths.evidence.loadFailed')));
      })
      .finally(() => {
        if (!cancelled) setStrengthsLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // `savedValidations` reads `reviewData.strengths`, which the page rewrites on every keystroke of
    // a verdict; depending on it would refetch the strengths list on each click. The seed is a
    // first-render concern only, so the effect is keyed on the SUBJECT.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee.id]);

  const statusOptions: StatusOption[] = [
    {
      value: 'accurate',
      label: t('performanceSnapshot.strengthsSkills.status.accurate'),
      Icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-600',
    },
    {
      value: 'not_really',
      label: t('performanceSnapshot.strengthsSkills.status.notReally'),
      Icon: XCircle,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-600',
    },
    {
      value: 'not_sure',
      label: t('performanceSnapshot.strengthsSkills.status.notSure'),
      Icon: HelpCircle,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-600',
    },
  ];

  /**
   * The evidence type, translated. The four cases are spelled out with literal keys rather than built
   * from the value, so `scripts/i18n-audit.mjs --strict` can actually see them. Anything else falls
   * through to the raw name — a label is never worth a blank row.
   */
  const evidenceTypeLabel = (type: EvidenceTypeName): string => {
    switch (type) {
      case 'SkillAdded':
        return t('performanceSnapshot.strengthsSkills.evidenceType.skillAdded');
      case 'Endorsement':
        return t('performanceSnapshot.strengthsSkills.evidenceType.endorsement');
      case 'ProficiencyChange':
        return t(
          'performanceSnapshot.strengthsSkills.evidenceType.proficiencyChange',
        );
      case 'Feedback':
        return t('performanceSnapshot.strengthsSkills.evidenceType.feedback');
      default:
        return type;
    }
  };

  /** `evidenceDate` is ISO-8601 UTC. Rendered in UTC so it cannot slide into the neighbouring month. */
  const formatEvidenceDate = (iso: string | null): string => {
    if (!iso) return '';
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) return '';
    // The UTC calendar day, re-built as a local date so the formatter (local fields) prints it unshifted.
    return appDate(new Date(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate()));
  };

  /**
   * The ONE writer of the per-snapshot validation blob. Both lists feed it, so a verdict on a
   * strength can never drop the skill verdicts already recorded that month (or the reverse) —
   * which is what two independent writers over one column would do.
   */
  const writeValidationBlob = (
    nextItems: ReviewItem[],
    nextStrengthStatuses: Record<number, string>,
  ) => {
    const skillEntries: ValidationEntry[] = nextItems.map((item) => ({
      name: item.name,
      type: item.type,
      status: item.status || 'pending',
    }));
    const strengthEntries: ValidationEntry[] = strengths.map((s) => ({
      name: s.label,
      type: 'strength',
      status: nextStrengthStatuses[s.id] || 'pending',
    }));
    // 🔴 Keys this panel does not own are PRESERVED, not dropped. Both dev seeders park an
    // ownership marker inside this blob — `seeder` (`PerformanceSnapshotSeeder.cs:209-218`) and
    // `fixture` (`PerformanceCalibrationSeeder.cs:868-874`) — and their ClearAsync deletes exactly
    // the rows carrying it. Replacing the whole object on the first verdict made a seeded row
    // unreclaimable, so the seeder had to fall back to deleting by period. Spreading costs nothing
    // and is idempotent once the blob is ours.
    const existing = (reviewData.strengths ?? {}) as Record<string, unknown>;
    onStrengthsValidationUpdate?.({
      ...existing,
      validations: [...skillEntries, ...strengthEntries],
    });
  };

  const handleStatusChange = (itemId: string, status: string) => {
    if (readOnly) return;
    const updated = items.map((item) =>
      item.id === itemId ? { ...item, status } : item,
    );
    setItems(updated);
    writeValidationBlob(updated, strengthStatuses);
  };

  /**
   * 🔴 CO-449 §3.5.1 — "Not really" MUST WRITE BOTH, and this is the whole point of the step.
   *
   *   1. the per-snapshot `strengthsValidation` blob — unchanged behaviour, it is the manager's
   *      record for THAT month; and
   *   2. `POST api/Skill/Strength/{id}/Suppress` — the durable fact on `UserStrength` that the
   *      nightly detector actually reads.
   *
   * The blob is per-snapshot and per-month, so a nightly derivation cannot and must not read it.
   * That is precisely why "Not really" has changed nothing until now. Wire only one of the two and
   * the button still changes nothing — it just looks like it did something.
   *
   * Any other verdict (accurate / not sure) LIFTS an existing suppression, so a mis-click is one
   * click to undo rather than a permanent deletion.
   */
  const handleStrengthStatusChange = async (
    strength: UserStrengthView,
    status: string,
  ) => {
    if (readOnly || !canSuppress) return;

    const previousStatuses = strengthStatuses;
    const nextStatuses = { ...strengthStatuses, [strength.id]: status };
    setStrengthStatuses(nextStatuses);
    setSuppressError(null);
    // Write (1) immediately — it is local page state and cannot fail.
    writeValidationBlob(items, nextStatuses);

    const shouldSuppress = status === 'not_really' && !strength.isSuppressed;
    const shouldUnsuppress = status !== 'not_really' && strength.isSuppressed;
    if (!shouldSuppress && !shouldUnsuppress) return;

    setSuppressBusyId(strength.id);
    try {
      const result = shouldSuppress
        ? await suppressStrength(strength.id)
        : await unsuppressStrength(strength.id);
      if (result?.errors?.length) throw result;
      // Flip the one flag locally rather than swallowing the returned view model whole: the write
      // endpoints hand back a RAW payload (the api module normalises only the read paths), and a
      // raw `confidence` would bypass the chip's normalizer.
      setStrengths((prev) =>
        prev.map((s) =>
          s.id === strength.id ? { ...s, isSuppressed: shouldSuppress } : s,
        ),
      );
    } catch (err) {
      // The durable half failed, so the local half is rolled back too — leaving the verdict on
      // screen would claim a suppression that does not exist anywhere the detector can see.
      setStrengthStatuses(previousStatuses);
      writeValidationBlob(items, previousStatuses);
      setSuppressError(getApiErrorMessage(err, t, t('strengths.suppress.failed')));
    } finally {
      setSuppressBusyId(null);
    }
  };

  /** Evidence is fetched on first expand and cached for the life of the panel. */
  const toggleEvidence = async (strengthId: number) => {
    if (expandedStrengthId === strengthId) {
      setExpandedStrengthId(null);
      return;
    }
    setExpandedStrengthId(strengthId);
    if (evidenceByStrength[strengthId]) return;

    setEvidenceLoadingId(strengthId);
    setEvidenceErrorIds((prev) => prev.filter((id) => id !== strengthId));
    try {
      const result = await getUserStrengthEvidence(strengthId);
      setEvidenceByStrength((prev) => ({
        ...prev,
        [strengthId]: result?.payload ?? [],
      }));
    } catch {
      // 🔴 The message is deliberately generic and the span is never logged: `quotedSpan` is a
      // verbatim excerpt of PRIVATE text (check-in manager notes, manager reflections).
      setEvidenceErrorIds((prev) => [...prev, strengthId]);
    } finally {
      setEvidenceLoadingId(null);
    }
  };

  const renderItem = (item: ReviewItem) => {
    const selectedOption = statusOptions.find(
      (opt) => opt.value === item.status,
    );

    return (
      <div
        key={item.id}
        className="bg-card border border-border rounded-lg p-4 hover:shadow-sm transition-shadow"
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h4 className="font-semibold text-foreground text-base">
              {item.name}
            </h4>
            <div className="flex items-center gap-3 mt-1">
              {item.proficiency && (
                <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                  {item.proficiency}
                </span>
              )}
              {item.endorsements > 0 && (
                <span className="text-xs text-muted-foreground">
                  {t('performanceSnapshot.strengthsSkills.endorsements', {
                    count: item.endorsements,
                  })}
                </span>
              )}
            </div>
          </div>
          {selectedOption && (
            <div
              className={`flex items-center space-x-1 px-2 py-1 rounded-md ${selectedOption.bgColor}`}
            >
              <selectedOption.Icon
                size={14}
                className={selectedOption.color}
              />
              <span
                className={`text-xs font-medium ${selectedOption.color}`}
              >
                {selectedOption.label}
              </span>
            </div>
          )}
        </div>
        <div className="flex space-x-2">
          {statusOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => handleStatusChange(item.id, option.value)}
              disabled={readOnly}
              className={`flex-1 flex items-center justify-center space-x-1 px-3 py-2 rounded-md border-2 transition-all duration-200 disabled:cursor-not-allowed ${
                item.status === option.value
                  ? `${option.borderColor} ${option.bgColor}`
                  : `border-border ${readOnly ? '' : 'hover:border-primary hover:bg-accent'}`
              }`}
            >
              <option.Icon
                size={16}
                className={
                  item.status === option.value
                    ? option.color
                    : 'text-muted-foreground'
                }
              />
              <span
                className={`text-xs font-medium ${
                  item.status === option.value
                    ? option.color
                    : 'text-muted-foreground'
                }`}
              >
                {option.label}
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  };

  /**
   * A chip label for a value that SHOULD be inside the union. The api module normalises it, but a
   * browser holding yesterday's bundle can meet a member this build was never taught, and a chip is
   * never worth a crash — an unknown confidence reads as the one that claims the least.
   */
  const confidenceLabel = (confidence: StrengthConfidence): string =>
    t(CONFIDENCE_LABEL_KEY[confidence] ?? CONFIDENCE_LABEL_KEY.Low);

  const confidenceChipClass = (confidence: StrengthConfidence): string =>
    CONFIDENCE_CHIP_CLASS[confidence] ?? CONFIDENCE_CHIP_CLASS.Low;

  const evidenceSourceLabel = (source: SkillEvidenceSourceType): string =>
    t(EVIDENCE_SOURCE_LABEL_KEY[source] ?? EVIDENCE_SOURCE_LABEL_KEY.Unknown);

  const renderStrengthEvidence = (strength: UserStrengthView) => {
    if (evidenceLoadingId === strength.id) {
      return (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 size={14} className="animate-spin" />
          {t('Loading')}
        </p>
      );
    }
    if (evidenceErrorIds.includes(strength.id)) {
      return (
        <p className="text-sm text-muted-foreground">
          {t('strengths.evidence.loadFailed')}
        </p>
      );
    }

    const rows = evidenceByStrength[strength.id] ?? [];
    if (rows.length === 0) {
      return (
        <p className="text-sm text-muted-foreground">
          {t('strengths.evidence.empty')}
        </p>
      );
    }

    return (
      <ul className="space-y-3">
        {rows.slice(0, MAX_QUOTED_SPANS).map((row) => (
          <li key={row.id} className="border-s-2 border-border ps-3">
            {row.quotedSpan && (
              <p className="flex gap-2 text-sm text-foreground">
                <Quote size={14} className="mt-1 shrink-0 text-muted-foreground" />
                <span className="italic">{row.quotedSpan}</span>
              </p>
            )}
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
              <span className="bg-muted rounded px-2 py-0.5">
                {evidenceSourceLabel(row.sourceType)}
              </span>
              {row.authorName && (
                <span>
                  {t('strengths.evidence.by')} {row.authorName}
                </span>
              )}
              {row.occurredAt && (
                <span dir="ltr" className="tabular-nums">
                  {formatEvidenceDate(row.occurredAt)}
                </span>
              )}
            </div>
          </li>
        ))}
      </ul>
    );
  };

  const renderStrength = (strength: UserStrengthView) => {
    const status = strengthStatuses[strength.id] ?? null;
    const selectedOption = statusOptions.find((opt) => opt.value === status);
    const expanded = expandedStrengthId === strength.id;
    const busy = suppressBusyId === strength.id;

    return (
      <div
        key={strength.id}
        className={`rounded-lg border p-4 ${
          strength.isSuppressed
            ? 'border-dashed border-border bg-muted'
            : 'border-border bg-card'
        }`}
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="flex-1">
            <h4
              className={`text-base font-semibold ${
                strength.isSuppressed
                  ? 'text-muted-foreground line-through'
                  : 'text-foreground'
              }`}
            >
              {strength.label}
            </h4>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
              <span
                className={`rounded px-2 py-0.5 text-xs font-medium ${confidenceChipClass(
                  strength.confidence,
                )}`}
              >
                {confidenceLabel(strength.confidence)}
              </span>
              <span className="text-xs text-muted-foreground">
                {t('strengths.byAuthors', { count: strength.distinctAuthorCount })}
              </span>
              {strength.lastConfirmedAt && (
                <span className="text-xs text-muted-foreground">
                  {t('strengths.lastConfirmed')}{' '}
                  <span dir="ltr" className="tabular-nums">
                    {formatEvidenceDate(strength.lastConfirmedAt)}
                  </span>
                </span>
              )}
              {strength.isAiOnly && (
                /* Matched no catalogue skill — say so rather than presenting a machine guess with
                   the same authority as a strength several colleagues named. */
                <span
                  title={t('strengths.aiProposedHint')}
                  className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground"
                >
                  {t('strengths.aiProposed')}
                </span>
              )}
            </div>
          </div>
          {selectedOption && !strength.isSuppressed && (
            <div
              className={`flex items-center gap-1 rounded-md px-2 py-1 ${selectedOption.bgColor}`}
            >
              <selectedOption.Icon size={14} className={selectedOption.color} />
              <span className={`text-xs font-medium ${selectedOption.color}`}>
                {selectedOption.label}
              </span>
            </div>
          )}
        </div>

        {strength.isSuppressed && (
          /* The durable state, plus the way back out of it. A suppression that can be lifted in one
             click needs no confirm dialog in front of it. */
          <div className="mb-3 flex flex-wrap items-center gap-3 rounded-md bg-card px-3 py-2">
            <span className="text-xs text-muted-foreground">
              {t('strengths.suppress.suppressed')}
            </span>
            {canSuppress && !readOnly && (
              <button
                type="button"
                onClick={() => handleStrengthStatusChange(strength, 'not_sure')}
                disabled={busy}
                className="flex items-center gap-1 text-xs font-medium text-foreground underline disabled:cursor-not-allowed"
              >
                {busy ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Undo2 size={14} />
                )}
                {t('strengths.suppress.undo')}
              </button>
            )}
          </div>
        )}

        {/* The three verdicts. They live on the STRENGTH cards because a strength is what they
            always claimed to be judging. They remain on the declared-skill cards below as well —
            see the note above that section. */}
        {canSuppress && !strength.isSuppressed && (
          <div className="flex gap-2">
            {statusOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleStrengthStatusChange(strength, option.value)}
                disabled={readOnly || busy}
                className={`flex flex-1 items-center justify-center gap-1 rounded-md border-2 px-3 py-2 transition-all duration-200 disabled:cursor-not-allowed ${
                  status === option.value
                    ? `${option.borderColor} ${option.bgColor}`
                    : `border-border ${readOnly ? '' : 'hover:border-primary hover:bg-accent'}`
                }`}
              >
                {busy && option.value === 'not_really' ? (
                  <Loader2 size={16} className="animate-spin text-muted-foreground" />
                ) : (
                  <option.Icon
                    size={16}
                    className={
                      status === option.value ? option.color : 'text-muted-foreground'
                    }
                  />
                )}
                <span
                  className={`text-xs font-medium ${
                    status === option.value ? option.color : 'text-muted-foreground'
                  }`}
                >
                  {option.value === 'not_really'
                    ? t('strengths.suppress.notReally')
                    : option.label}
                </span>
              </button>
            ))}
          </div>
        )}

        <button
          type="button"
          onClick={() => toggleEvidence(strength.id)}
          className="mt-3 flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
          aria-expanded={expanded}
        >
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          {t('strengths.evidence.title')} ({strength.evidenceCount})
        </button>
        {expanded && <div className="mt-3">{renderStrengthEvidence(strength)}</div>}
      </div>
    );
  };

  /* CO-447 — the OBSERVED section. Absent entirely when the caller may not read it (403). */
  const showObservedSection = !strengthsDenied;

  /**
   * The bottom "Review Progress" strip counts BOTH lists now that both are validatable. Counting
   * only the skills would have quietly reported "0 not really" on a month whose only verdict was a
   * suppressed strength — the exact reading the manager just contradicted.
   */
  const countVerdicts = (status: string): number =>
    items.filter((s) => s.status === status).length +
    strengths.filter((s) => strengthStatuses[s.id] === status).length;

  // Empty state when no skills data exists at all.
  // CO-440 — in-period evidence counts as data: a snapshot can carry evidence for a skill that never
  // reaches the lifetime top-10, and hiding the whole tab behind an empty top-10 would bury it.
  // CO-447 — an observed strength is data too, and it is the MOST load-bearing thing on this tab:
  // a person can have several strengths named by colleagues and no declared skill at all, and
  // "No Skills Data Available" over that would be false. The `strengthsLoading` guard keeps the
  // empty state from flashing before the first fetch settles.
  if (items.length === 0 && !inPeriod && !strengthsLoading && strengths.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-12 text-center">
        <Award size={48} className="mx-auto mb-4 opacity-50" />
        <h3 className="text-lg font-semibold text-foreground mb-2">
          {t('performanceSnapshot.strengthsSkills.noData')}
        </h3>
        <p className="text-sm text-muted-foreground">
          {t('performanceSnapshot.strengthsSkills.noDataDescription')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-500/10 via-indigo-500/10 to-purple-500/10 border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center">
            <Award size={20} color="white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground">
              {t('performanceSnapshot.strengthsSkills.title')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t('performanceSnapshot.strengthsSkills.subtitle', {
                name: employee.name,
              })}
            </p>
          </div>
        </div>
      </div>

      {/* OBSERVED-STRENGTHS SECTION (CO-447 / CO-449)
          🔴 FIRST, above both skill blocks, because it is the only block on this tab that is about
          what OTHER PEOPLE observed. Everything below it is the person's own declared skill
          profile. Until now this tab called that profile "Strengths" and offered three buttons to
          validate it, which is why "Not really" could never mean anything. */}
      {showObservedSection && (
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <Sparkles size={20} className="text-purple-600" />
            <h3 className="text-lg font-semibold text-foreground">
              {t('performanceSnapshot.strengthsSkills.observedLabel')}
            </h3>
            {!strengthsLoading && strengths.length > 0 && (
              <span className="text-sm text-muted-foreground">
                ({t('strengths.count', { count: strengths.length })})
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            {t('strengths.sectionSubtitle')}
          </p>

          {suppressError && (
            <p className="mb-4 rounded-lg border border-border bg-muted p-3 text-sm text-foreground">
              {suppressError}
            </p>
          )}

          {strengthsLoading ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 size={16} className="animate-spin" />
              {t('Loading')}
            </p>
          ) : strengthsError ? (
            <p className="rounded-lg border border-border bg-muted p-4 text-sm text-muted-foreground">
              {strengthsError}
            </p>
          ) : strengths.length === 0 ? (
            /* Deliberately neutral. "No observed strengths yet" is a statement about how much has
               been WRITTEN about this person, not a low score, so it gets no red and no zero. */
            <div className="rounded-lg border border-border bg-muted p-4">
              <p className="text-sm text-foreground">{t('strengths.empty')}</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('strengths.emptyHint')}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {strengths.map((strength) => renderStrength(strength))}
            </div>
          )}
        </div>
      )}

      {/* IN-PERIOD SECTION (CO-440)
          What actually happened to this person's skills inside the snapshot's month. It is rendered
          FIRST and labelled, because the list below it is a lifetime one: until CO-440 that lifetime
          list sat under a single unqualified heading and silently read as "this month".
          Absent on pre-CO-440 rows, which is why the whole block is conditional. */}
      {inPeriod && (
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center space-x-2 mb-1">
            <CalendarClock size={20} className="text-emerald-600" />
            <h3 className="text-lg font-semibold text-foreground">
              {t('performanceSnapshot.strengthsSkills.inPeriodLabel')}
            </h3>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            {t('performanceSnapshot.strengthsSkills.inPeriodSummary', {
              count: inPeriod.evidenceCount,
              skills: inPeriod.distinctSkills,
            })}
          </p>

          {inPeriod.skills.length === 0 ? (
            /* Measured, and nothing happened. Deliberately neutral: this is a statement about the
               month, not a low score, so it gets no red and no zero-filled meter. */
            <p className="text-sm text-muted-foreground bg-muted border border-border rounded-lg p-4">
              {t('performanceSnapshot.strengthsSkills.inPeriodEmpty')}
            </p>
          ) : (
            <ul className="space-y-2">
              {inPeriod.skills.map((evidence, idx) => (
                <li
                  key={`${evidence.skillName}_${idx}`}
                  className="flex flex-wrap items-center gap-x-3 gap-y-1 border border-border rounded-lg px-4 py-3"
                >
                  <span className="font-medium text-foreground">
                    {evidence.skillName}
                  </span>
                  <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                    {evidenceTypeLabel(evidence.evidenceType)}
                  </span>
                  {evidence.evidenceDate && (
                    /* A date reverses under `ar` unless it is pinned LTR — the established idiom
                       everywhere a raw date or period token is rendered in this view. */
                    <span
                      dir="ltr"
                      className="text-xs text-muted-foreground tabular-nums ms-auto"
                    >
                      {formatEvidenceDate(evidence.evidenceDate)}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* LIFETIME SECTION (CO-440)
          The declared skill profile as it stands TODAY — unbounded, top 10 by endorsement then
          proficiency. It is labelled as lifetime so it stops being read as the month's evidence. */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-2 mb-1">
          {inPeriod ? (
            <History size={20} className="text-purple-600" />
          ) : (
            <Sparkles size={20} className="text-purple-600" />
          )}
          <h3 className="text-lg font-semibold text-foreground">
            {t('performanceSnapshot.strengthsSkills.lifetimeLabel')}
          </h3>
          <span className="text-sm text-muted-foreground">
            ({items.filter((s) => s.status).length}/{items.length}{' '}
            {t('performanceSnapshot.strengthsSkills.reviewed')})
          </span>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          {t('performanceSnapshot.strengthsSkills.lifetimeHint')}
        </p>
        {/* 🔴 The three verdict buttons stay on these cards as well as on the strength cards above.
            The spec says "move" them; moving them would leave EVERY snapshot with no validation
            control at all until the nightly detector has run (it is behind
            `Engagement:StrengthDetectionEnabled`, which ships FALSE), and would strand the recorded
            verdicts on the 162 legacy `yyyy-Qn` rows with no way to change them. Two lists, two
            verdict sets, kept apart by `type` in the blob. */}
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground bg-muted border border-border rounded-lg p-4">
            {t('performanceSnapshot.strengthsSkills.lifetimeEmpty')}
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {items.map((item) => renderItem(item))}
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="bg-muted border border-border rounded-lg p-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            {t('performanceSnapshot.strengthsSkills.reviewProgress')}
</span>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <CheckCircle size={16} className="text-green-600" />
              <span className="font-medium text-foreground">
                {countVerdicts('accurate')}{' '}
                {t('performanceSnapshot.strengthsSkills.status.accurate')}
              </span>
            </div>
            <div className="flex items-center space-x-1">
              <XCircle size={16} className="text-red-600" />
              <span className="font-medium text-foreground">
                {countVerdicts('not_really')}{' '}
                {t('performanceSnapshot.strengthsSkills.status.notReally')}
              </span>
            </div>
            <div className="flex items-center space-x-1">
              <HelpCircle size={16} className="text-amber-600" />
              <span className="font-medium text-foreground">
                {countVerdicts('not_sure')}{' '}
                {t('performanceSnapshot.strengthsSkills.status.notSure')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SurfedStrengthsSkillsPanel;
